/**
 * @fileoverview A founding link opened by somebody who already has an account.
 *
 * Signing up through grow.gardengameplan.com/?founding=CODE makes a founding
 * gardener of the new account (CompleteSignup sends the code). Somebody who
 * already has an account, signed in or signing in after opening the link,
 * never signs up; for them the kept code (arrivalSource) is claimed here,
 * once, as soon as their account is loaded. While the code is open they are
 * made a founding gardener too, and told so in a toast: free until the
 * founding day, then the founder price for their first year ("Grow More is
 * free for you until February 28. Then it's $39 for your first year."; no
 * price for as long as they stay), or, for a subscriber, their founder
 * price. A wrong or closed code says nothing, as it says nothing at sign-up.
 * When every founding place is taken (result.full; L7) nobody is made one,
 * and a kind toast says so ("The founding places are all taken. Thank you for
 * wanting one. Your garden carries on just as it is."); the code is forgotten.
 *
 * The first year's price in the toast is said whole by the server in our language (priceWords: "$39 for your first year",
 * "$39 el primer año"; C101); the toast's own words only carry the day around it.
 *
 * Renders nothing.
 *
 * Where: mounted once in the Layout (shared/components/layout/Layout.tsx), so
 * it runs on every signed-in page.
 * API: POST /billing/founding { code } (claimFounding); then the billing
 * status and the advice (the Today brief, and the advice before a postcode)
 * are read again.
 * Settings: the kept code in shared/lib/arrivalSource (from ?founding= on
 * arrival).
 * Words (en/es): founding.title, .freeUntil, .freeUntilPrice, .free,
 * .subscriber, .subscriberPrice, .already, .fullTitle, .full.
 * Watch out: a failed request resets the guard but nothing re-runs the
 * effect, so the claim is tried again on the next page load, not at once.
 * A failure on our side (a 500, no connection; D31, lib/serverFailure) is
 * reported to Sentry; nothing is said, as the claim runs unasked.
 *
 * @module FoundingClaim
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { foundingDay } from '../lib/foundingDay';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useToast } from '@/shared/components/ui/use-toast';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { claimFounding } from '@/shared/api/billing';
import { forgetFoundingCode, keptFoundingCode } from '@/shared/lib/arrivalSource';
import { BILLING_QUERY_KEY } from '../lib/useBilling';
import { READS_QUERY_KEY } from '@/features/tips/useTips';
import { TODAY_BRIEF_QUERY_KEY } from '@/config/tunables/today';

/** Claims a kept founding code for the signed-in account, once, and says what came of it. */
export const FoundingClaim: React.FC = () => {
  const { mysqlUser } = useAuth();
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userId = mysqlUser?.user_id ?? null;
  /** The account a claim was started for, so a re-render does not claim twice. */
  const claimedFor = React.useRef<number | null>(null);

  React.useEffect(() => {
    const code = keptFoundingCode();
    if (!userId || !code || claimedFor.current === userId) return;
    claimedFor.current = userId;
    void (async () => {
      try {
        const result = await claimFounding(code);
        // Claimed, or refused: either way it is not asked again.
        forgetFoundingCode();
        // Every place taken: a kind line, and nothing else changes for them (L7).
        if (!result.founding && result.full) {
          toast({ title: t('founding.fullTitle'), description: t('founding.full') });
          return;
        }
        if (!result.founding) return;
        void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
        // The advice for a founding gardener (their welcome line) comes with the brief, and before a postcode on its own.
        void queryClient.invalidateQueries({ queryKey: [TODAY_BRIEF_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: READS_QUERY_KEY });
        const access = result.access;
        const until = foundingDay(access?.founding?.freeUntil, language) || null;
        const price = access?.founding?.price ?? null;
        // The first year's price said whole by the server in our language (it sends none for a code that is not founding).
        const firstYear = result.priceWords ?? null;
        const description = result.already
          ? t('founding.already')
          : access?.active
            ? (price ? t('founding.subscriberPrice', { price }) : t('founding.subscriber'))
            : until
              ? (firstYear ? t('founding.freeUntilPrice', { date: until, price: firstYear }) : t('founding.freeUntil', { date: until }))
              : t('founding.free');
        // The line that says when the first year starts and that there is nothing to do before it (L6), the server's words.
        const starts = !result.already && until && result.startsWords ? ` ${result.startsWords}` : '';
        toast({ title: t('founding.title'), description: `${description}${starts}` });
      } catch (error) {
        // Not reached, or the server failed: keep the code and try on the next visit.
        if (isServerFailure(error)) reportServerFailure(error, 'Founding claim');
        claimedFor.current = null;
      }
    })();
  }, [userId, queryClient, toast, t, language]);

  return null;
};
